import type { AutocompleteOption } from "@/components/Autocomplete";
import { api } from "@/lib/apiClient";
import { guestTypeLabel } from "@/lib/guestType";
import { formatGrossFromNet } from "@/lib/money";
import { CHARGE_RULE_LABELS } from "@/lib/chargeRuleLabels";
import type {
  Account,
  Article,
  ChargeRule,
  Guest,
  GuestListResponse,
  Pitch,
  PitchCategory,
  Supplier,
} from "@/types/camping";

function matches(haystack: string | null | undefined, needle: string): boolean {
  if (!needle) return true;
  return String(haystack || "")
    .toLowerCase()
    .includes(needle.toLowerCase());
}

export function guestDisplayName(guest: Guest): string {
  return `${guest.last_name}, ${guest.first_name}`;
}

function guestDescription(guest: Guest): string {
  const parts = [guestTypeLabel(guest.guest_type)];
  if (guest.email) parts.push(guest.email);
  if (guest.phone) parts.push(guest.phone);
  if (guest.city) parts.push(guest.city);
  return parts.join(" · ");
}

export function guestToOption(guest: Guest): AutocompleteOption<Guest> {
  return {
    key: guest.id,
    label: guestDisplayName(guest),
    description: guestDescription(guest),
    value: guest,
  };
}

export async function guestSource(query: string): Promise<AutocompleteOption<Guest>[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const res = await api.get<GuestListResponse>(
    `/api/v1/guests?q=${encodeURIComponent(q)}&limit=50`,
  );
  return (res.items ?? []).map(guestToOption);
}

export function pitchDisplayName(pitch: Pitch): string {
  return pitch.label ? `${pitch.code} – ${pitch.label}` : pitch.code;
}

export function pitchToOption(pitch: Pitch): AutocompleteOption<Pitch> {
  return {
    key: pitch.id,
    label: pitchDisplayName(pitch),
    description: pitch.category_name ?? undefined,
    value: pitch,
  };
}

export function pitchSource(
  pitches: Pitch[],
  query: string,
): Promise<AutocompleteOption<Pitch>[]> {
  const q = query.trim().toLowerCase();
  const filtered = pitches
    .filter(
      (pitch) =>
        !q ||
        pitch.code.toLowerCase().includes(q) ||
        (pitch.label?.toLowerCase().includes(q) ?? false) ||
        (pitch.category_name?.toLowerCase().includes(q) ?? false),
    )
    .slice(0, 50);
  return Promise.resolve(filtered.map(pitchToOption));
}

export function articleDisplayName(article: Article): string {
  return `${article.article_number} – ${article.name}`;
}

export function articleToOption(article: Article): AutocompleteOption<Article> {
  const priceHint =
    article.current_price != null
      ? formatGrossFromNet(article.current_price, article.tax_rate_percent)
      : undefined;
  return {
    key: article.id,
    label: articleDisplayName(article),
    description: priceHint ? `Preis: ${priceHint}` : undefined,
    value: article,
  };
}

export function articleSource(
  articles: Article[],
  query: string,
): Promise<AutocompleteOption<Article>[]> {
  const q = query.trim();
  const filtered = articles
    .filter(
      (article) =>
        matches(article.article_number, q) ||
        matches(article.name, q) ||
        matches(article.barcode, q),
    )
    .slice(0, 50);
  return Promise.resolve(filtered.map(articleToOption));
}

export function chargeRuleLabel(rule: ChargeRule): string {
  const type = CHARGE_RULE_LABELS[rule.rule_type] ?? rule.rule_type;
  const article =
    rule.article_number && rule.article_name
      ? `${rule.article_number} – ${rule.article_name}`
      : (rule.article_name ?? rule.article_number ?? "");
  const parts = [type];
  if (rule.pitch_category_name) parts.push(rule.pitch_category_name);
  if (rule.guest_type) parts.push(guestTypeLabel(rule.guest_type));
  if (article) parts.push(article);
  return parts.join(" · ");
}

export function chargeRuleToOption(rule: ChargeRule): AutocompleteOption<ChargeRule> {
  return {
    key: rule.id,
    label: chargeRuleLabel(rule),
    description: rule.is_active ? undefined : "Inaktiv",
    value: rule,
  };
}

export function chargeRuleSource(
  rules: ChargeRule[],
  query: string,
): Promise<AutocompleteOption<ChargeRule>[]> {
  const q = query.trim();
  const filtered = rules
    .filter(
      (rule) =>
        matches(chargeRuleLabel(rule), q) ||
        matches(rule.article_number, q) ||
        matches(rule.article_name, q) ||
        matches(rule.pitch_category_name, q),
    )
    .slice(0, 50);
  return Promise.resolve(filtered.map(chargeRuleToOption));
}

export function supplierToOption(supplier: Supplier): AutocompleteOption<Supplier> {
  return {
    key: supplier.id,
    label: supplier.name,
    description: supplier.is_active ? undefined : "Inaktiv",
    value: supplier,
  };
}

export function supplierSource(
  suppliers: Supplier[],
  query: string,
): Promise<AutocompleteOption<Supplier>[]> {
  const q = query.trim();
  const filtered = suppliers
    .filter((supplier) => matches(supplier.name, q))
    .slice(0, 50);
  return Promise.resolve(filtered.map(supplierToOption));
}

export function accountToOption(account: Account): AutocompleteOption<Account> {
  return {
    key: account.id,
    label: `${account.account_number} – ${account.name}`,
    value: account,
  };
}

export function accountSource(
  accounts: Account[],
  query: string,
): Promise<AutocompleteOption<Account>[]> {
  const q = query.trim();
  const filtered = accounts
    .filter(
      (account) => matches(account.account_number, q) || matches(account.name, q),
    )
    .slice(0, 50);
  return Promise.resolve(filtered.map(accountToOption));
}

export function pitchCategoryToOption(
  category: PitchCategory,
): AutocompleteOption<PitchCategory> {
  return {
    key: category.id,
    label: category.name,
    value: category,
  };
}

export function pitchCategorySource(
  categories: PitchCategory[],
  query: string,
): Promise<AutocompleteOption<PitchCategory>[]> {
  const q = query.trim();
  const filtered = categories
    .filter((category) => matches(category.name, q))
    .slice(0, 50);
  return Promise.resolve(filtered.map(pitchCategoryToOption));
}
